/**
 * Schedule Component
 * Displays class schedule in a visual, interactive format
 */

import styles from './Schedule.module.css';

export class Schedule {
  constructor(options = {}) {
    const { scheduleData = null } = options;
    // Default sample data - replace with actual data
    this.scheduleData = scheduleData || this.getDefaultSchedule();
  }

  getDefaultSchedule() {
    return {
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      classes: [
        // Note: Actual schedule needs to be updated from fall-schedule page
        // These are placeholder classes based on typical ballet school structure
        { day: 'Monday', time: '4:00 PM', name: 'Pre-Ballet', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Monday', time: '5:00 PM', name: 'Level 1', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Monday', time: '6:00 PM', name: 'Level 2-3', instructor: 'Ms. Baratova', level: 'Intermediate' },
        { day: 'Tuesday', time: '4:00 PM', name: 'Pre-Ballet', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Tuesday', time: '5:00 PM', name: 'Level 1', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Tuesday', time: '6:00 PM', name: 'Advanced', instructor: 'Mr. Amato', level: 'Advanced' },
        { day: 'Wednesday', time: '4:00 PM', name: 'Pre-Ballet', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Wednesday', time: '5:00 PM', name: 'Level 1', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Wednesday', time: '6:00 PM', name: 'Adult Ballet', instructor: 'Ms. Baratova', level: 'All Levels' },
        { day: 'Thursday', time: '4:00 PM', name: 'Pre-Ballet', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Thursday', time: '5:00 PM', name: 'Level 1', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Thursday', time: '6:00 PM', name: 'Level 2-3', instructor: 'Ms. Baratova', level: 'Intermediate' },
        { day: 'Friday', time: '4:00 PM', name: 'Pre-Ballet', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Friday', time: '5:00 PM', name: 'Contemporary', instructor: 'Ms. Carili', level: 'Intermediate' },
        { day: 'Saturday', time: '10:00 AM', name: 'Pre-Ballet', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Saturday', time: '11:00 AM', name: 'Level 1', instructor: 'Ms. Baratova', level: 'Beginner' },
        { day: 'Saturday', time: '12:00 PM', name: 'Level 2-3', instructor: 'Ms. Baratova', level: 'Intermediate' },
        { day: 'Saturday', time: '1:00 PM', name: 'Advanced', instructor: 'Mr. Amato', level: 'Advanced' }
      ]
    };
  }

  render() {
    const section = document.createElement('section');
    section.className = styles.schedule;
    section.setAttribute('id', 'schedule');

    const container = document.createElement('div');
    container.className = styles.container;

    // Header
    const header = document.createElement('div');
    header.className = styles.header;

    const title = document.createElement('h2');
    title.className = styles.title;
    title.textContent = 'Class Schedule';
    header.appendChild(title);

    const subtitle = document.createElement('p');
    subtitle.className = styles.subtitle;
    subtitle.textContent = '2025 - 2026 Class Schedule';
    header.appendChild(subtitle);

    container.appendChild(header);

    // Filter buttons
    const filters = document.createElement('div');
    filters.className = styles.filters;
    
    const allButton = document.createElement('button');
    allButton.className = `${styles.filterButton} ${styles.active}`;
    allButton.textContent = 'All Classes';
    allButton.setAttribute('data-filter', 'all');
    
    const levels = ['Beginner', 'Intermediate', 'Advanced', 'All Levels'];
    levels.forEach(level => {
      const button = document.createElement('button');
      button.className = styles.filterButton;
      button.textContent = level;
      button.setAttribute('data-filter', level.toLowerCase());
      filters.appendChild(button);
    });

    filters.insertBefore(allButton, filters.firstChild);
    container.appendChild(filters);

    // Schedule grid
    const scheduleGrid = document.createElement('div');
    scheduleGrid.className = styles.scheduleGrid;

    // Group classes by day
    const classesByDay = {};
    this.scheduleData.classes.forEach(cls => {
      if (!classesByDay[cls.day]) {
        classesByDay[cls.day] = [];
      }
      classesByDay[cls.day].push(cls);
    });

    // Create day columns
    this.scheduleData.days.forEach(day => {
      const dayColumn = document.createElement('div');
      dayColumn.className = styles.dayColumn;
      dayColumn.setAttribute('data-day', day.toLowerCase());

      const dayHeader = document.createElement('h3');
      dayHeader.className = styles.dayHeader;
      dayHeader.textContent = day;
      dayColumn.appendChild(dayHeader);

      const classesList = document.createElement('div');
      classesList.className = styles.classesList;

      const dayClasses = classesByDay[day] || [];
      if (dayClasses.length === 0) {
        const empty = document.createElement('div');
        empty.className = styles.emptyDay;
        empty.textContent = 'No classes';
        classesList.appendChild(empty);
      } else {
        dayClasses.forEach(cls => {
          const classCard = this.createClassCard(cls);
          classesList.appendChild(classCard);
        });
      }

      dayColumn.appendChild(classesList);
      scheduleGrid.appendChild(dayColumn);
    });

    container.appendChild(scheduleGrid);

    // CTA
    const cta = document.createElement('div');
    cta.className = styles.cta;

    const ctaText = document.createElement('p');
    ctaText.textContent = 'Ready to enroll?';
    cta.appendChild(ctaText);

    const ctaButton = document.createElement('a');
    ctaButton.href = '/join';
    ctaButton.className = styles.ctaButton;
    ctaButton.textContent = 'Enroll Now';
    cta.appendChild(ctaButton);

    container.appendChild(cta);
    section.appendChild(container);

    // Add filter functionality
    this.setupFilters(filters, scheduleGrid);

    return section;
  }

  createClassCard(cls) {
    const card = document.createElement('div');
    card.className = styles.classCard;
    card.setAttribute('data-level', cls.level.toLowerCase());

    const time = document.createElement('div');
    time.className = styles.time;
    time.textContent = cls.time;
    card.appendChild(time);

    const name = document.createElement('div');
    name.className = styles.className;
    name.textContent = cls.name;
    card.appendChild(name);

    if (cls.instructor) {
      const instructor = document.createElement('div');
      instructor.className = styles.instructor;
      instructor.textContent = `with ${cls.instructor}`;
      card.appendChild(instructor);
    }

    const level = document.createElement('div');
    level.className = `${styles.level} ${styles[cls.level.toLowerCase().replace(' ', '')]}`;
    level.textContent = cls.level;
    card.appendChild(level);

    return card;
  }

  setupFilters(filtersContainer, scheduleGrid) {
    const filterButtons = filtersContainer.querySelectorAll(`.${styles.filterButton}`);
    
    filterButtons.forEach(button => {
      button.addEventListener('click', () => {
        // Update active state
        filterButtons.forEach(btn => btn.classList.remove(styles.active));
        button.classList.add(styles.active);

        // Filter classes
        const filter = button.getAttribute('data-filter');
        const classCards = scheduleGrid.querySelectorAll(`.${styles.classCard}`);

        classCards.forEach(card => {
          if (filter === 'all') {
            card.style.display = '';
          } else {
            const cardLevel = card.getAttribute('data-level');
            if (cardLevel.includes(filter)) {
              card.style.display = '';
            } else {
              card.style.display = 'none';
            }
          }
        });

        // Hide empty days if all classes filtered out
        const dayColumns = scheduleGrid.querySelectorAll(`.${styles.dayColumn}`);
        dayColumns.forEach(dayColumn => {
          const visibleCards = dayColumn.querySelectorAll(`.${styles.classCard}[style=""]`);
          const classesList = dayColumn.querySelector(`.${styles.classesList}`);
          const empty = dayColumn.querySelector(`.${styles.emptyDay}`);
          
          if (visibleCards.length === 0 && empty) {
            empty.style.display = '';
          } else if (empty) {
            empty.style.display = 'none';
          }
        });
      });
    });
  }
}
